import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spin, Alert, Space, Button, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { StateService } from '../../services/api/stateService';
import { TransitionService } from '../../services/api/transitionService';
import { ArtifactItemState } from '../../types/artifactItems';
import { ArtifactItemTransition } from '../../types/artifactItems';
import { StateMachineGraph, StateMachineNode, StateMachineEdge } from '../../types/stateMachine';

interface StateMachineDiagramProps {
  artifactId: string;
  currentItemStateId?: string; // Highlight current state if item is selected
  onStateClick?: (stateId: string) => void;
  onTransitionClick?: (transitionId: string) => void;
  compact?: boolean;
}

export const StateMachineDiagram: React.FC<StateMachineDiagramProps> = ({
  artifactId,
  currentItemStateId,
  onStateClick,
  onTransitionClick,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<ArtifactItemState[]>([]);
  const [transitions, setTransitions] = useState<ArtifactItemTransition[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (artifactId) {
      void loadData();
    }
  }, [artifactId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statesData, transitionsData] = await Promise.all([
        StateService.getStatesByArtifact(artifactId),
        TransitionService.getTransitionsByArtifact(artifactId),
      ]);

      setStates(statesData || []);
      setTransitions(transitionsData || []);
    } catch (err: any) {
      console.error('Error loading state machine data:', err);
      setError(err.message || 'Failed to load state machine data');
    } finally {
      setLoading(false);
    }
  };

  const graph: StateMachineGraph = useMemo(() => {
    const nodes: StateMachineNode[] = states.map((state) => ({
      id: state.id,
      label: state.name,
      type: state.state_type || 'PROCESSITEM',
      isInitial: state.is_initial,
      isFinal: state.is_final,
      data: { state },
    }));

    const edges: StateMachineEdge[] = transitions.map((transition) => {
      // Handle different field name variations
      const fromStateId = (transition as any).from_state_id || transition.input_state_ref || '';
      const toStateId = (transition as any).to_state_id || transition.output_state_ref || '';
      
      return {
        id: transition.id,
        source: fromStateId,
        target: toStateId,
        label: (transition as { transition_name?: string }).transition_name ?? transition.name,
        isAutomatic: (transition as { is_automatic?: boolean; isAutomatic?: boolean }).is_automatic ?? (transition as { isAutomatic?: boolean }).isAutomatic ?? false,
        data: { transition },
      };
    });

    return { nodes, edges };
  }, [states, transitions]);

  if (loading) {
    return (
      <Card size={compact ? 'small' : 'default'}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading state machine diagram...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card size={compact ? 'small' : 'default'}>
        <Alert message="Error" description={error} type="error" showIcon />
      </Card>
    );
  }

  if (states.length === 0) {
    return (
      <Card size={compact ? 'small' : 'default'}>
        <Alert
          message="No States"
          description="No states defined for this artifact. Create states to visualize the state machine."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  // Simple text-based visualization (can be enhanced with React Flow later)
  return (
    <Card
      title={
        <Space>
          <span>State Machine Diagram</span>
          <Button icon={<ReloadOutlined />} size="small" onClick={loadData} loading={loading} />
        </Space>
      }
      size={compact ? 'small' : 'default'}
    >
      <div style={{ padding: 16 }}>
        <div style={{ marginBottom: 24 }}>
          <h4>States ({states.length})</h4>
          <Space wrap>
            {states.map((state) => {
              const isCurrent = currentItemStateId === state.id;
              const isInitial = state.is_initial;
              const isFinal = state.is_final;

              let color = 'default';
              if (isCurrent) color = 'blue';
              else if (isInitial) color = 'green';
              else if (isFinal) color = 'red';

              return (
                <Tag
                  key={state.id}
                  color={color}
                  style={{ cursor: onStateClick ? 'pointer' : 'default', padding: '4px 8px' }}
                  onClick={() => onStateClick?.(state.id)}
                >
                  {state.name}
                  {isInitial && ' (Initial)'}
                  {isFinal && ' (Final)'}
                  {isCurrent && ' (Current)'}
                </Tag>
              );
            })}
          </Space>
        </div>

        <div>
          <h4>Transitions ({transitions.length})</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {transitions.map((transition) => {
              const t = transition as { from_state_id?: string; to_state_id?: string };
              const fromStateId = t.from_state_id ?? transition.input_state_ref ?? '';
              const toStateId = t.to_state_id ?? transition.output_state_ref ?? '';
              const fromState = states.find((s) => s.id === fromStateId);
              const toState = states.find((s) => s.id === toStateId);

              return (
                <div
                  key={transition.id}
                  style={{
                    padding: 8,
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    cursor: onTransitionClick ? 'pointer' : 'default',
                    backgroundColor: '#fafafa',
                  }}
                  onClick={() => onTransitionClick?.(transition.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag>{fromState?.name || 'Unknown'}</Tag>
                    <span>→</span>
                    <Tag>{toState?.name || 'Unknown'}</Tag>
                    <span style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
                      {(transition as { transition_name?: string }).transition_name ?? transition.name}
                    </span>
                    {((transition as { is_automatic?: boolean; isAutomatic?: boolean }).is_automatic ?? (transition as { isAutomatic?: boolean }).isAutomatic) && (
                      <Tag color="orange">
                        Automatic
                      </Tag>
                    )}
                  </div>
                  {transition.description && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      {transition.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {graph.nodes.length === 0 && graph.edges.length === 0 && (
          <Alert
            message="Empty State Machine"
            description="This artifact has no states or transitions defined."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    </Card>
  );
};

